import { useLocation, useModel } from '@umijs/max';
import { Select } from 'antd';
import React, { useMemo } from 'react';
import { normalizePoolId, setCurrentPoolId } from '@/utils/currentPool';

/** 展示格式：泳池名称(空间缩写·场馆缩写) */
function formatPoolSwitcherLabel(p: {
  poolName?: string;
  spaceName?: string;
  gymName?: string;
}): string | undefined {
  const pool = p.poolName?.trim();
  const space = p.spaceName?.trim();
  const gym = p.gymName?.trim();
  const abbr = [space, gym].filter(Boolean).join('·');
  if (pool && abbr) {
    return `${pool}(${abbr})`;
  }
  if (pool) {
    return pool;
  }
  return abbr || undefined;
}

/**
 * 设备/安全子系统侧栏顶部的泳池切换器。
 * 仅在 pathname 以 /device 或 /safety 开头，且泳池数 > 1 时展示。
 * 「项目配置」(/project/*) 不展示：各页自行手选泳池。
 * 泳池 ID 一律按字符串处理（雪花 Long，不可 Number）。
 */
const PoolSwitcher: React.FC = () => {
  const location = useLocation();
  const { initialState, setInitialState } = useModel('@@initialState');

  const onPoolScopedPath =
    location.pathname.startsWith('/device') ||
    location.pathname.startsWith('/safety');

  const options = useMemo(
    () =>
      (initialState?.pools ?? [])
        .map((p) => {
          const value = normalizePoolId(p.id);
          if (value == null) {
            return null;
          }
          return {
            label: formatPoolSwitcherLabel(p) || `泳池 ${value}`,
            value,
          };
        })
        .filter(
          (item): item is { label: string; value: string } => item != null,
        ),
    [initialState?.pools],
  );

  const selectedValue = useMemo(() => {
    const current = normalizePoolId(initialState?.currentPoolId);
    if (current == null) {
      return undefined;
    }
    // 必须能在 options 里精确匹配，否则 Select 会错显其它项
    return options.some((o) => o.value === current) ? current : undefined;
  }, [initialState?.currentPoolId, options]);

  // 非设备/安全页，或仅 0/1 个泳池（无需切换）时不渲染，避免侧栏顶部留白
  if (!onPoolScopedPath || options.length <= 1) {
    return null;
  }

  const handleChange = (poolId: string) => {
    const next = normalizePoolId(poolId);
    if (next == null) {
      return;
    }
    setCurrentPoolId(next);
    setInitialState((s) => ({
      ...s,
      currentPoolId: next,
    }));
    // 切换泳池后刷新，确保各页请求带上新的 poolId
    window.location.reload();
  };

  return (
    <Select
      style={{ width: '100%', maxWidth: 280, marginBottom: 8, height: 32 }}
      placeholder="选择泳池"
      options={options}
      value={selectedValue}
      onChange={handleChange}
      optionFilterProp="label"
      showSearch
      className="lt-sider-pool-switcher"
    />
  );
};

export default PoolSwitcher;
